import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import BrandLockup from "@/v2/components/BrandLockup";
import { errText } from "@/v2/lib/hooks";
import "@/v2/v2.css";
import { Eye, EyeOff, Sparkles, ShieldCheck, UserPlus } from "lucide-react";

export default function V2Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`أهلًا بك، ${u.name}`);
      if (u.needs_onboarding) navigate("/onboarding", { replace: true });
      else if (u.is_approved === false) navigate("/pending", { replace: true });
      else navigate(u.role === "dept_manager" ? "/dept" : u.role === "employee" ? "/dashboard" : "/admin", { replace: true });
    } catch (err) {
      setError(errText(err, "تعذّر تسجيل الدخول، تحقق من البيانات."));
    } finally { setLoading(false); }
  };

  return (
    <div className="v2-root brand-auth min-h-screen grid lg:grid-cols-2" dir="rtl">
      {/* visual side */}
      <div className="brand-auth-story relative hidden lg:flex flex-col justify-between p-12 overflow-hidden v2-grid v2-pattern">
        <div className="absolute top-[-10%] left-[-5%] w-[460px] h-[460px] rounded-full bg-[#91cab8]/15 blur-3xl v2-float" />
        <div className="absolute bottom-[-8%] right-[5%] w-[380px] h-[380px] rounded-full bg-[#bb9661]/12 blur-3xl v2-float" style={{ animationDelay: "2s" }} />
        <div className="relative z-10"><BrandLockup tone="dark" /></div>
        <div className="relative z-10">
          <div className="mb-4 inline-flex"><span className="v2-glass px-4 py-1.5 rounded-full text-sm text-[#91cab8] border-[#91cab8]/30 flex items-center gap-2"><Sparkles size={16} /> منصة تعلّم ذكية من الجيل الجديد</span></div>
          <h1 className="text-5xl font-extrabold leading-[1.15] mb-5">رحلة <span className="v2-gold-text">تميّز</span> رقمية<br />تصنع الخبراء</h1>
          <p className="text-[#bcc8cf] leading-relaxed max-w-md text-lg">عشرون يومًا من التحديات التفاعلية، تقييم ذكي بالذكاء الاصطناعي، ونظام مستويات ونقاط يحفّزك للتقدّم كل يوم.</p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-[#bcc8cf] text-sm"><ShieldCheck size={16} className="text-[#91cab8]" /> بيئة آمنة — منصة داخلية للموظفين</div>
      </div>

      {/* form side */}
      <div className="brand-auth-main flex items-center justify-center p-6 sm:p-12">
        <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          onSubmit={submit} data-testid="v2-login-form" className="w-full max-w-md">
          <div className="lg:hidden mb-8"><BrandLockup tone="light" /></div>
          <h2 className="text-3xl font-extrabold mb-2">تسجيل الدخول</h2>
          <p className="text-[#bcc8cf] mb-8">مرحبًا بعودتك، تابع رحلتك نحو التميّز.</p>

          {error && <div data-testid="v2-login-error" className="mb-4 rounded-2xl bg-[#B45309]/15 border border-[#B45309]/40 text-[#F7A08A] px-4 py-3 text-sm">{error}</div>}

          <label className="block text-sm font-semibold mb-2 text-[#bcc8cf]">الاسم أو البريد الإلكتروني</label>
          <input data-testid="v2-login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="text"
            placeholder="اكتب اسمك كما سجّلته، أو بريدك الإلكتروني"
            className="w-full h-13 rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 mb-5 outline-none focus:border-[#bb9661]/50 focus:ring-2 focus:ring-[#91cab8]/30 text-white placeholder:text-[#bcc8cf]/60" />

          <label className="block text-sm font-semibold mb-2 text-[#bcc8cf]">كلمة المرور</label>
          <div className="relative mb-4">
            <input data-testid="v2-login-password" value={password} onChange={(e) => setPassword(e.target.value)} type={show ? "text" : "password"}
              className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 pe-12 outline-none focus:border-[#bb9661]/50 focus:ring-2 focus:ring-[#91cab8]/30 text-white" />
            <button type="button" onClick={() => setShow(!show)} className="absolute inset-y-0 left-3 flex items-center text-[#bcc8cf]">{show ? <EyeOff size={20} /> : <Eye size={20} />}</button>
          </div>

          <div className="flex items-center justify-between mb-6 text-sm">
            <label className="flex items-center gap-2 text-[#bcc8cf] cursor-pointer">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 accent-[#91cab8]" /> تذكرني
            </label>
            <button type="button" onClick={() => toast.info("يرجى التواصل مع إدارة المنصة لإعادة تعيين كلمة المرور.")} className="v2-gold-text font-bold">نسيت كلمة المرور؟</button>
          </div>

          <button data-testid="v2-login-submit" type="submit" disabled={loading} className="v2-btn w-full">
            {loading ? "جارٍ الدخول..." : "دخول إلى المنصة"}
          </button>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-[#bcc8cf]">أو</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <Link to="/register" data-testid="v2-login-to-register"
            className="mt-5 v2-btn-ghost w-full flex items-center justify-center gap-2">
            <UserPlus size={17} /> إنشاء حساب جديد
          </Link>
        </motion.form>
      </div>
    </div>
  );
}
